import type { AttributeReviewFieldMappingsQuery } from "@/graphql/generated";
import type { AgentAnswers, LeafRejection } from "./reviewRow.types";
import { deriveSourceAttributeLabel } from "./deriveSourceAttributeLabel";
import { extractSourceEnumValues } from "./extractSourceEnumValues";
import {
  isApprovedAttributeReviewStatus,
  isRejectedAttributeReviewStatus,
} from "./reviewQueueStatus";

type AttributeReviewFieldMappingItem = NonNullable<
  AttributeReviewFieldMappingsQuery["attributeReviewFieldMappings"]
>["items"][number];

function buildBooleanIdentityEnumMapping(
  values: string[],
): Record<string, string> {
  const normalizedValues = new Set(values.map((value) => value.toLowerCase()));
  if (normalizedValues.size !== 2) return {};
  if (!normalizedValues.has("true") || !normalizedValues.has("false"))
    return {};
  return Object.fromEntries(values.map((value) => [value, value]));
}

function getApprovalString(
  record: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): string | null {
  const value = record[camelKey] ?? record[snakeKey];
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function buildSuggestedValueMappings(value: unknown): Record<string, string> {
  if (!Array.isArray(value)) return {};
  return Object.fromEntries(
    value.flatMap((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return [];
      }
      const record = item as Record<string, unknown>;
      const sourceValue = getApprovalString(
        record,
        "sourceValue",
        "source_value",
      );
      const suggestedCanonicalValue = getApprovalString(
        record,
        "suggestedCanonicalValue",
        "suggested_canonical_value",
      );
      if (!sourceValue || !suggestedCanonicalValue) return [];
      return [[sourceValue, suggestedCanonicalValue]];
    }),
  );
}

/**
 * Map API field mapping item to the shape expected by AttributeReviewRow (mock-compatible).
 * Use generated codegen hooks from @/graphql/generated for data fetching.
 * One function per file (single method per file).
 */
export function mapFieldMappingToRow(
  item: AttributeReviewFieldMappingItem,
  _index: number,
): Record<string, unknown> {
  const enumMappings =
    item.enumMappings && typeof item.enumMappings === "object"
      ? (item.enumMappings as Record<string, unknown>)
      : {};
  const sourceEnumValues = extractSourceEnumValues(
    item.sourceEnumValues,
    item.marketplaceRawJson,
  );
  const displayEnumMappings =
    Object.keys(enumMappings).length > 0
      ? {
          ...buildSuggestedValueMappings(item.valueApprovals),
          ...enumMappings,
        }
      : {
          ...buildBooleanIdentityEnumMapping(sourceEnumValues),
          ...buildSuggestedValueMappings(item.valueApprovals),
        };
  const allowedTargetValues = Array.from(
    new Set(
      Object.values(displayEnumMappings)
        .filter((value): value is string => typeof value === "string")
        .filter(Boolean),
    ),
  );
  const valueResolutionStatuses = Object.values(
    (item.valueResolutionStatus ?? {}) as Record<string, unknown>,
  );

  return {
    id: item.sourceFieldId,
    // Raw backend field status (e.g. 'matched' vs 'APPROVED'). Only a
    // not-yet-committed 'matched' row still needs an explicit APPROVE to finalize the session.
    status: item.status ?? null,
    // Marketplace requirement of the source attribute (server-derived): REQUIRED |
    // CONDITIONALLY_REQUIRED | OPTIONAL. Drives the required-star before rejecting.
    requirementLevel: item.requirementLevel ?? null,
    kind: item.kind ?? null,
    folder: item.folder ?? null,
    leaf: item.leaf ?? null,
    leafSource: item.leafSource ?? null,
    pathPreview: item.pathPreview ?? null,
    pool: item.pool ?? null,
    values: item.values ?? [],
    warnings: item.warnings ?? [],
    leafRejection: (item.leafRejection ?? null) as LeafRejection | null,
    remapped: item.remapped ?? null,
    path: item.path ?? null,
    pathType: item.pathType ?? null,
    ownPath: item.ownPath ?? null,
    link: item.link ?? false,
    linkOwner: item.linkOwner ?? null,
    linkField: item.linkField ?? null,
    hidden: item.hidden ?? false,
    pastAnswer: item.pastAnswer ?? false,
    copyOf: item.copyOf ?? null,
    showKind: item.showKind ?? null,
    review: item.review ?? null,
    agent: (item.agent ?? null) as AgentAnswers | null,
    suggested: item.suggested ?? [],
    sourceAttribute: deriveSourceAttributeLabel(
      item.sourceLabel,
      item.sourceFieldId,
    ),
    marketplaceRawJson: item.marketplaceRawJson ?? {},
    suggestedTargetAttribute: item.canonicalPath ?? null,
    canonicalDatatype: item.canonicalDatatype ?? null,
    arrayIndex: item.arrayIndex ?? null,
    aiBand: item.aiBand ?? null,
    gateVerdict: item.gateVerdict ?? null,
    registryStatus: item.registryStatus,
    reason: item.llmReasoning || item.lowConfidenceReason || "",
    llmReasoning: item.llmReasoning ?? null,
    lowConfidenceReason: item.lowConfidenceReason ?? null,
    reviewReason: item.reviewReason ?? null,
    reviewDetail: item.reviewDetail ?? null,
    answers: item.answers ?? [],
    mappingSource: item.mappingSource ?? null,
    // Reviewer-facing provenance bucket (REUSED | AI_REUSED | AI_GENERATED | null)
    // derived server-side. The UI renders this; the rerank/method/candidate
    // telemetry it replaced is no longer carried onto the row.
    provenance: item.provenance ?? null,
    sourceValue: null,
    sourceEnumValues,
    sourceEnumValueLabels:
      (item.sourceEnumValueLabels as
        | Record<string, string>
        | null
        | undefined) ?? {},
    sourceEnumValueCount: item.sourceEnumValueCount ?? sourceEnumValues.length,
    enumMappingCount: item.enumMappingCount ?? Object.keys(enumMappings).length,
    valueApprovalCount:
      item.valueApprovalCount ??
      (Array.isArray(item.valueApprovals) ? item.valueApprovals.length : 0),
    enumDetailsTruncated: item.enumDetailsTruncated ?? false,
    allowedTargetValues,
    valueApprovals: item.valueApprovals ?? [],
    proposedDomainKey: item.proposedDomainKey ?? null,
    proposedDomainRationale: item.proposedDomainRationale ?? null,
    domainKey: item.domainKey ?? null,
    domainSource: item.domainSource ?? null,
    domainExists: item.domainExists ?? false,
    valueResolutionStatus: item.valueResolutionStatus,
    valueResolutionSources: item.valueResolutionSources,
    valueStatus:
      valueResolutionStatuses.length > 0 &&
      valueResolutionStatuses.every((status) => status === "resolved_globally")
        ? "resolved_globally"
        : undefined,
    pendingCanonicalExpansion: item.pendingCanonicalExpansion,
    canonicalExpansionRequestId: item.canonicalExpansionRequestId ?? null,
    duplicateCanonicalPath: item.duplicateCanonicalPath ?? false,
    canonicalValue: undefined,
    needsReview: item.needsReview,
    decisionStatus: isRejectedAttributeReviewStatus(item.status)
      ? "REJECTED"
      : isApprovedAttributeReviewStatus(item.status)
        ? "APPROVED"
        : "NEEDS_REVIEW",
    alternativeTargets: [],
    suggestedEnumMapping: displayEnumMappings,
  };
}
